import PropTypes from 'prop-types';
import qs from 'qs';
import { isUndefined, omitBy } from 'lodash';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import deferHydration from '$util/deferHydration';
import MainLayout from '$layout/MainLayout';
import { getReassuranceQuery } from '$components/composite/Reassurance/gql';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { WithResponsiveGlobalStyles } from '$util/theme/withResponsive';
import { PartnerBanner } from '$components/VrmLookupSection/components/PartnerBanner';
import { getTopNavBannerQuery } from '$components/pages/checkout/quotes/TopNavBannerWithRedeem/gql';
import { HeroSection } from '@/components/molecules/HeroSection';
import { setAcquisitionMethodPartner } from '../util/setHomeCookies';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'index_partner';

const ReassuranceBlueIconsTheme = deferHydration(() => import('$components/ReassuranceBlueIconsTheme'), {
  hydrationId: '$components/ReassuranceBlueIconsTheme',
  trigger: 'view',
});

const HowItWorksSection = deferHydration(() => import('$components/HowItWorksSection'), {
  hydrationId: '$components/HowItWorksSection',
  trigger: 'view',
});

const CategoryCardsSection = deferHydration(() => import('$components/CategoryCardsSection'), {
  hydrationId: '$components/CategoryCardsSection',
  trigger: 'view',
});

const OurMechanicsSection = deferHydration(() => import('$components/OurMechanicsSection'), {
  hydrationId: '$components/OurMechanicsSection',
  trigger: 'view',
});

const GarageNetworkSection = deferHydration(() => import('$components/GarageNetworkSection'), {
  hydrationId: '$components/GarageNetworkSection',
  trigger: 'view',
});

const GaragesSection = deferHydration(() => import('$components/GaragesSection'), {
  hydrationId: '$components/GaragesSection',
  trigger: 'view',
});

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

function PartnerPage({ content = {}, ...props }) {
  const { garages } = content;
  const { partnerData, pathname, pageTypeFromPath } = props;

  setAcquisitionMethodPartner(pathname);

  return (
    <MainLayout
      seo={{
        noindex: false,
        nofollow: false,
        title: 'Car service, MOT and repair the easy way | Fixter',
        metaDescription:
          `Looking for hassle-free MOT, car service, repair or maintenance? ` +
          `Let Fixter take the strain! Our local experts will collect and deliver ` +
          `your car to your door and make sure you get the right job done at a fair price.`,
      }}
    >
      <WithResponsiveGlobalStyles />
      {pageTypeFromPath === 'partner' && <PartnerBanner pathname={pathname} partnerData={partnerData} />}
      <HeroSection
        locale={Locale.EN_GB}
        heading="Hassle-free MOT"
        subheading="We take your car to the best local garage, with free collection and delivery. All covered by 12 month warranty. Easy."
      />
      <ReassuranceBlueIconsTheme />
      <HowItWorksSection {...props} className="partner" />
      <CategoryCardsSection />
      <OurMechanicsSection />
      <GarageNetworkSection />
      <GaragesSection garages={garages} />
      <TeamSupportSection source={PAGE_NAME} />
    </MainLayout>
  );
}

export const getServerSideProps = async function (entities) {
  const { req, res, query: urlQuery, preview: isPreviewMode } = entities;
  const { pageType, pageMeta, pathname, pageTypeFromPath } = qs.parse(urlQuery);
  // eslint-disable-next-line no-template-curly-in-string
  const bindings = eval('require(`${SERVER_PATH}/bindings`)();');
  const { getContentByPath, getCmsContent } = bindings.newObject('store.pageContent');

  const locale = getLocaleFromHostname(req.headers.host || '');
  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      getReassuranceQuery,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getTopNavBannerQuery,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  const content = omitBy((await getContentByPath('/')) || {}, (val) => typeof val === 'undefined');
  const partnerData = (await getCmsContent(pathname, 'partner')) || {};
  if (partnerData) {
    /**
     * Use the pathname as partner id since partner name can have chars that cause
     * issues in other places where this is used (eg. spaces)
     */
    setAcquisitionMethodPartner(pathname, entities);
    // `undefined` cannot be serialized as JSON, so we need to convert it to `null`
    if (partnerData.logoUrl === undefined) {
      partnerData.logoUrl = null;
    }
    if (partnerData.campaignId === undefined) {
      partnerData.campaignId = null;
    }
  }
  const props = omitBy(
    { content, pathname, pageType, pageMeta, pageTypeFromPath, partnerData, cmsContent },
    isUndefined
  );

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return { props };
};

PartnerPage.propTypes = {
  content: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
  pageType: PropTypes.array.isRequired,
  pageMeta: PropTypes.object.isRequired,
  partnerData: PropTypes.object.isRequired,
};

export default PartnerPage;
